<script>
import lazyLoadComponent from '@router/lazyload-component'
import BaseButtonDialog from '@components/BaseButtonDialog'

import { genderFilter, orderStatusMap, orderStatusFilter, orderPaymentMethodMap, orderPaymentMethodFilter, orderSourceMap, orderSourceFilter, productTypeList } from '@utils/filter-value-to-text'
import formatDate, { formatRequestData, formatLunarDate } from '@utils/format-date'
import { isTempleModule, isLightModule } from '@utils'

import pageMixin from '@src/mixins/pagination'

import { PortalTarget } from 'portal-vue'

export default {
  page: {
    title: '訂單查詢',
    meta: [{ name: 'description', content: 'Order List' }],
  },
  components: {
    BaseButtonDialog,
    BaseStateLabel: () => lazyLoadComponent(import('@components/BaseStateLabel')),
    OrderDetail: () => lazyLoadComponent(import('@components/OrderDetail')),
    LightRegister: () => lazyLoadComponent(import('./components/LightRegister')),
    PortalTarget,
  },
  filters: {
    genderFilter,
    orderStatusFilter,
    orderPaymentMethodFilter,
    orderSourceFilter,
    formatDate,
    formatLunarDate,
  },
  mixins: [pageMixin],
  data() {
    return {
      drawerVisible: false,
      drawerOrderData: '',
      filterData: {
        status: '',
        source: '',
        dateType: 'create', // create 訂單成立日期、 use 訂單使用日期
        startDate: '',
        endDate: '',
        paymentMethod: 'All',
        productType: '',
      },
      statusList: Object.freeze(orderStatusMap),
      sourceList: Object.freeze(orderSourceMap),
      paymentMethodList: Object.freeze(orderPaymentMethodMap),
      productTypeList: Object.freeze(productTypeList),
      pickerOptions: {
        shortcuts: [
          {
            text: '最近一週',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
              picker.$emit('pick', [start, end])
            },
          },
          {
            text: '最近一個月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              picker.$emit('pick', [start, end])
            },
          },
          {
            text: '最近三個月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              picker.$emit('pick', [start, end])
            },
          },
        ],
      },
      loadedOrders: [],
      queryProductName: '',
      tryingToGetOrder: false,
      tryingToExport: false,
      submitForm: [],
      submitRules: {
        title: [{ required: true, message: 'empty', trigger: 'change' }],
      },
    }
  },
  computed: {
    filterDateRange: {
      get() {
        return [this.filterData.startDate, this.filterData.endDate]
      },
      set(value) {
        const [start, end] = value || ['', '']
        this.filterData.startDate = start
        this.filterData.endDate = end
      },
    },
    fetchDataParams() {
      const routeQuery = this.$route.query
      const queryData = Object.assign(routeQuery, this.pageQuery)
      const { startDate, endDate, dateType, ...filterDataWithoutDate } = this.filterData
      const searchWithDate = startDate && endDate
      const searchParams = searchWithDate
        ? Object.assign(this.filterData, {
            startDate: formatRequestData(startDate),
            endDate: formatRequestData(endDate),
          })
        : filterDataWithoutDate
      return Object.assign(searchParams, queryData)
    },
  },
  watch: {
    currentPage: {
      handler: 'tryToFetchList',
      immediate: true,
    },
  },
  methods: {
    isTempleModule,
    isLightModule,
    async handleExport() {
      if (this.tryingToExport) return
      this.tryingToExport = true
      const { fileName, csvData } = await this.$store.dispatch('order/simpleExport', this.fetchDataParams)
      const csvFormatData = csvData.map((row) => Object.values(row).join(',')).join('\n')

      const blob = new Blob(['\uFEFF', csvFormatData], {
        type: 'text/csv;charset=utf-8;',
      })

      const url = window.URL.createObjectURL(blob)

      const tempLink = document.createElement('a')
      tempLink.download = fileName
      tempLink.href = url
      tempLink.click()
      window.URL.revokeObjectURL(url)
      this.tryingToExport = false
    },
    tableRowClassName({ row }) {
      const rowStatus = row.status
      const usedStatus = row.groupSize / row.usedSize
      if (rowStatus === 0) return 'order--canceled'
      if (usedStatus === 1) return 'order--used'
    },
    handleDrawerClosed() {
      this.$refs.orderDetail.mode = 'view'
    },
    async tryToGetOrder(orderId) {
      this.tryingToGetOrder = true
      this.drawerOrderData = await this.$store.dispatch('order/fetchSingle', orderId)
      this.tryingToGetOrder = false
      this.drawerVisible = true
    },
    async tryToFetchList() {
      const { items, productName, total } = await this.$store.dispatch('order/fetchList', this.fetchDataParams)

      this.loadedOrders = items
      this.queryProductName = productName
      this.$nextTick(() => {
        this.$refs.pagination.updateTotal(total)
      })
    },
    clearSearchQuery() {
      this.$router.push(this.$route.path)
    },
    formatCustomerInfo(data) {
      return data.row.codes.reduce((prev, next) => {
        return (
          prev +
          unescape(next.customerInfo)
            .replace(/\\n(?!.*\\n)/g, '<br>')
            .replace(/\\n/g, ' ') +
          '<br>'
        )
      }, '')
    },
    onExpand(row, expandedRows) {
      this.$nextTick(()=>{
        if(this.$refs[`subTable_${row.id}`] !== undefined)
          this.$refs[`subTable_${row.id}`].doLayout()
      })
    }
  },
}
</script>

<template>
  <div>
    <BaseTableSearch v-slot="{ fields }" :default-value.sync="filterData" @reset="tryToFetchList">
      <el-form-item prop="status">
        <el-select v-model="fields.status" placeholder="選擇訂單狀態" @change="tryToFetchList">
          <el-option label="所有訂單狀態" value></el-option>
          <el-option v-for="(status, statusValue) in statusList" :key="statusValue" :label="status.text" :value="statusValue"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="paymentMethod">
        <el-select v-model="fields.paymentMethod" placeholder="選擇付款方式" @change="tryToFetchList">
          <el-option label="所有付款方式" value="All"></el-option>
          <el-option v-for="(label, value) in paymentMethodList" :key="value" :label="label" :value="value"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="source">
        <el-select v-model="fields.source" placeholder="選擇訂單來源" @change="tryToFetchList">
          <el-option label="所有訂單來源" value></el-option>
          <el-option v-for="(label, value) in sourceList" :key="value" :label="label" :value="value"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-date-picker
          v-model="filterDateRange"
          type="daterange"
          align="right"
          unlink-panels
          range-separator="-"
          start-placeholder="起始日期"
          end-placeholder="結束日期"
          :picker-options="pickerOptions"
          @change="tryToFetchList"
        ></el-date-picker>
      </el-form-item>
      <el-form-item prop="productType">
        <el-select v-model="fields.productType" placeholder="訂購項目類型" @change="tryToFetchList">
          <el-option label="所有項目類型" value></el-option>
          <el-option v-for="(type, typeValue) in productTypeList" :key="typeValue" v-can:productType="typeValue" :label="type.name" :value="typeValue"></el-option>
        </el-select>
      </el-form-item>
    </BaseTableSearch>
    <el-table
      v-loading.fullscreen.lock="tryingToGetOrder"
      element-loading-text="取得訂單資訊中"
      :data="loadedOrders"
      :default-sort="{ prop: 'created', order: 'descending' }"
      :row-class-name="tableRowClassName"
      @expand-change="onExpand"
    >
      <el-table-column v-slot="{ row }" type="expand">
        <LightRegister v-if="isLightModule(row.productType)" :default-value="row" @refresh="tryToFetchList"></LightRegister>
        <el-table :ref="`subTable_${row.id}`" v-else-if="isTempleModule(row.productType)" :data="row.params" border>
          <el-table-column prop="title" label="購買方案" width="150"></el-table-column>
          <el-table-column v-slot="{ ...params }" label="登記資訊">
            <div v-html="formatCustomerInfo(params)"></div>
          </el-table-column>
          <el-table-column prop="qty" label="數量" width="180"></el-table-column>
        </el-table>
        <el-table v-else :data="row.params" border>
          <el-table-column prop="title" label="購買方案"></el-table-column>
          <el-table-column prop="qty" label="數量" width="180"></el-table-column>
          <el-table-column prop="sizeTotal" label="憑證總數"></el-table-column>
          <el-table-column prop="used" label="已使用憑證數量"></el-table-column>
        </el-table>
      </el-table-column>
      <el-table-column v-slot="{ row }" label="#訂單編號" width="220" sortable>
        <div>{{ `#${row.id}` }}</div>
        <div>{{ row.created | formatDate('dateTime') }}</div>
      </el-table-column>
      <el-table-column v-slot="{ row }" label="訂購項目">
        <div>
          <b>{{ row.productName }}</b>
        </div>
        <div v-if="isLightModule(row.productType)">{{ row.date | formatLunarDate }}</div>
        <div v-else-if="row.productType === 'treasury'">{{ row.date | formatDate }}</div>
        <div v-else>{{ row.date | formatDate }} {{ row.time }}</div>
      </el-table-column>
      <el-table-column v-slot="{ row }" label="狀態與來源" width="120" sortable>
        <BaseStateLabel :state="row.status | orderStatusFilter('color')" text>{{ row.status | orderStatusFilter('text') }}</BaseStateLabel>
        <br />
        <BaseStateLabel state="info" text>{{ row.source | orderSourceFilter }}</BaseStateLabel>
      </el-table-column>
      <el-table-column v-slot="{ row }" label="付款方式" width="100" sortable>{{ row.paymentMethod | orderPaymentMethodFilter }}</el-table-column>
      <el-table-column v-slot="{ row }" label="登記人資訊">
        <div>{{ row.fullName }} {{ row.gender | genderFilter }}</div>
        <div>{{ `${row.phoneCode} ${row.phoneNumber}` }}</div>
      </el-table-column>

      <el-table-column v-slot="{ row }">
        <el-button @click="tryToGetOrder(row.id)">查看詳情</el-button>
      </el-table-column>
    </el-table>
    <BasePagination ref="pagination" :current-page.sync="currentPage" />
    <BaseButtonDialog button-tooltip="匯出訂單 CSV 檔" button-icon-name="file-export" only-button floating @opened="handleExport" />
    <el-drawer :visible.sync="drawerVisible" append-to-body size="50%" @closed="handleDrawerClosed">
      <PortalTarget name="drawer-header" slot="title"></PortalTarget>
      <OrderDetail ref="orderDetail" :default-value.sync="drawerOrderData" @update="tryToFetchList" />
    </el-drawer>
  </div>
</template>
